/**
 * **Les colonnes de Regrouper** (chantier theme-analyse, ticket 03 ; maquette `quatre-themes.html`,
 * `regroupe`, `.titre-g`) : du calcul, pas de JSX.
 *
 * En Analyse, G range les bulles de la Carte en colonnes, une par groupe, égales, titrées
 * « NOM · n ». C'est la seule exception à « ce fichier n'en invente aucune » de `planDeLaCarte.ts`,
 * et seulement à l'écran : les colonnes se calculent ici, depuis les groupes que le serveur compte
 * (`GET /api/feuille/:id/groupes`), à chaque dessin. Rien n'est stocké ; les places du serveur ne changent
 * pas, et la carte d'avant revient telle quelle quand on ne regroupe plus.
 *
 * Le repère est celui des places du serveur (le titre de la feuille en 0, 0) : les colonnes sont
 * centrées sur lui, les titres en haut, les bulles empilées dessous sans se recouvrir, d'après la
 * hauteur de chacune (estimée par le serveur, ou mesurée à l'écran si elle est plus grande).
 */
import type { Point } from './geometrieCarte';
import { boitesDesBulles, type Boite } from './placeDesEtiquettes';

/** Un groupe, tel que la carte le pose : son titre déjà dit, ses bulles dans l'ordre. */
export interface ColonneDeLaCarte {
  cle: string;
  /** « À FAIRE · 3 » (`titreDeColonne`). */
  titre: string;
  ids: readonly string[];
}

export interface TitreDeColonne {
  cle: string;
  titre: string;
  x: number;
  y: number;
}

export interface ColonnesPosees {
  places: Record<string, Point>;
  titres: TitreDeColonne[];
  /** Les bulles et les titres : ce que la caméra cadre, et que la borne du regard garde en vue. */
  boites: Boite[];
}

/** Une bulle fait 210 px de large (`.noeud`) : la colonne lui laisse de quoi respirer. */
export const LARGEUR_DE_COLONNE = 260;
/** La hauteur d'une bulle dont on ne sait rien encore (`BULLE` de `placeDesEtiquettes.ts`). */
const HAUTEUR_D_UNE_BULLE = 64;
/** Entre deux bulles d'une colonne. */
const ECART = 22;
/** Du titre de la colonne au haut de sa première bulle. */
const SOUS_LE_TITRE = 22;
/** Le titre : deux lignes de 12 px en capitales au plus (une adresse longue). */
const DEMI_HAUTEUR_DU_TITRE = 18;

export function colonnesDeLaCarte(
  colonnes: readonly ColonneDeLaCarte[],
  hauteurs: Record<string, number>
): ColonnesPosees {
  const places: Record<string, Point> = {};
  const titres: TitreDeColonne[] = [];
  const titreY = 0;
  colonnes.forEach((colonne, i) => {
    const x = (i - (colonnes.length - 1) / 2) * LARGEUR_DE_COLONNE;
    titres.push({ cle: colonne.cle, titre: colonne.titre, x, y: titreY });
    let haut = titreY + DEMI_HAUTEUR_DU_TITRE + SOUS_LE_TITRE;
    for (const id of colonne.ids) {
      const hauteur = hauteurs[id] ?? HAUTEUR_D_UNE_BULLE;
      places[id] = { x, y: haut + hauteur / 2 };
      haut += hauteur + ECART;
    }
  });
  if (colonnes.length === 0) return { places, titres, boites: [] };
  return {
    places,
    titres,
    boites: [
      ...titres.map((t) => ({ x: t.x, y: t.y, demiLargeur: LARGEUR_DE_COLONNE / 2, demiHauteur: DEMI_HAUTEUR_DU_TITRE })),
      ...boitesDesBulles(places, hauteurs),
    ],
  };
}
